/**
 * Security configuration for the authentication system
 */

export const SECURITY_CONFIG = {
  // Rate limiting configurations
  rateLimit: {
    login: {
      maxRequests: 5,
      windowMinutes: 15,
      message:
        "Too many login attempts. Please try again in {minutes} minutes.",
    },
    register: {
      maxRequests: 3,
      windowMinutes: 60,
      message:
        "Too many registration attempts. Please try again in {minutes} minutes.",
    },
    otpVerify: {
      maxRequests: 5,
      windowMinutes: 15,
      message:
        "Too many verification attempts. Please try again in {minutes} minutes.",
    },
    otpResend: {
      maxRequests: 3,
      windowMinutes: 10,
      message: "Too many OTP requests. Please try again in {minutes} minutes.",
    },
    forgotPassword: {
      maxRequests: 3,
      windowMinutes: 60,
      message:
        "Too many password reset requests. Please try again in {minutes} minutes.",
    },
  },

  // Account lockout configuration
  accountLockout: {
    maxFailedAttempts: 5,
    checkWindowMinutes: 15,
    // Progressive lockout durations (in minutes)
    lockoutDurations: [1, 5, 10, 30],
    reasons: {
      tooManyAttempts: "Too many failed login attempts",
      suspiciousActivity: "Suspicious activity detected",
    },
  },

  // Password policies
  password: {
    minLength: 8,
    requireUppercase: true,
    requireLowercase: true,
    requireNumbers: true,
    requireSpecialChars: false,
    tempPassword: "RockBoard",
  },

  // OTP configuration
  otp: {
    length: 6,
    expiryMinutes: 10,
  },

  // Token expiry times
  tokens: {
    passwordResetExpiryMinutes: 60,
    accountDeletionExpiryMinutes: 60,
  },

  // Session configuration
  session: {
    maxAgeDays: 30,
    absoluteTimeoutDays: 90,
  },

  // Security headers recommendations
  headers: {
    contentSecurityPolicy: {
      "default-src": ["'self'"],
      "script-src": ["'self'", "'unsafe-inline'", "'unsafe-eval'"],
      "style-src": ["'self'", "'unsafe-inline'"],
      "img-src": ["'self'", "data:", "https:"],
      "connect-src": ["'self'"],
    },
    strictTransportSecurity: {
      maxAge: 31536000,
      includeSubDomains: true,
      preload: true,
    },
  },
} as const;

/**
 * Get rate limit message with minutes replaced
 */
export function getRateLimitMessage(template: string, minutes: number): string {
  return template.replace("{minutes}", minutes.toString());
}

/**
 * Get lockout duration based on number of previous lockouts
 */
export function getProgressiveLockoutDuration(lockoutCount: number): number {
  const durations = SECURITY_CONFIG.accountLockout.lockoutDurations;
  // Use the last duration if we've exceeded the array length
  const index = Math.min(lockoutCount, durations.length - 1);
  return durations[index];
}
